import type { LanguageCode } from "config/languages";
import languages from "config/languages";
import type { MenuExtendedProps } from "config/menus/menus.d";
import type { ServerSideStoryblokPages } from "types/storyblok";
import getDomainFromLanguage from "utils/getDomainFromLanguage";
import { getLocaleFromUrl } from "utils/getLocale";
import { getPageUrl } from "utils/getPageUrl";
import { getSlugWithoutLocale } from "utils/getSlugWithoutLocale";
import { isDefaultLanguage } from "utils/isDefaultLanguage";
import type { ResolvedMenuPagePaths } from "./menuUtils.d";

const DEFAULT_FALLBACK_LANGUAGE: LanguageCode = "en";

const getStoryblokMenuHrefWithDomain = (language: LanguageCode, href: string) =>
  href && `${getDomainFromLanguage(language)}${href}`;

const getHrefForCurrentLanguage = (language: LanguageCode, href: string) => {
  const isDefaultPage = isDefaultLanguage(getLocaleFromUrl(href));

  const hrefForCurrentLanguage = isDefaultPage ? `/${language}/${getSlugWithoutLocale(href)}` : href;

  return getStoryblokMenuHrefWithDomain(language, hrefForCurrentLanguage);
};

const getStoryblokMenuHref = (
  pageAlternates: Record<LanguageCode, string>,
  language: LanguageCode,
  parentLanguage: LanguageCode,
  fallback: LanguageCode
) => {
  const originalPageHref = pageAlternates[language];
  if (originalPageHref) return getHrefForCurrentLanguage(language, originalPageHref);

  const defaultPageHref = pageAlternates[`${parentLanguage}-default`];
  if (defaultPageHref) return getHrefForCurrentLanguage(language, defaultPageHref);

  const parentPageHref = pageAlternates[parentLanguage];
  if (parentPageHref) return getHrefForCurrentLanguage(language, parentPageHref);

  const fallbacPagekHref = pageAlternates[fallback];
  if (fallbacPagekHref) return getHrefForCurrentLanguage(language, fallbacPagekHref);

  const defaultFallbackPageHref = pageAlternates[DEFAULT_FALLBACK_LANGUAGE];
  if (defaultFallbackPageHref) return getHrefForCurrentLanguage(DEFAULT_FALLBACK_LANGUAGE, defaultFallbackPageHref);

  return "";
};

export const resolveMenuPagePaths = (
  menu: MenuExtendedProps[],
  language: LanguageCode,
  parentLanguage: LanguageCode,
  serverSideStoryblokPages: ServerSideStoryblokPages
): ResolvedMenuPagePaths[] => {
  const filterNotSupportedLanguages = (item: MenuExtendedProps) =>
    !item?.languages || item.languages.includes(language);
  return menu.filter(filterNotSupportedLanguages).map((item) => {
    const { storyblokId, fallback, page, subMenu } = item;

    if (storyblokId) {
      const pageAlternates = serverSideStoryblokPages[storyblokId];

      if (!pageAlternates) return item;

      const href = getStoryblokMenuHref(pageAlternates, language, parentLanguage, fallback);

      return {
        href: href.endsWith("/") ? href : `${href}/`,
        ...item,
      };
    }

    if (Object.prototype.hasOwnProperty.call(item, "subMenu")) {
      return {
        ...item,
        subMenu: resolveMenuPagePaths(subMenu, language, parentLanguage, serverSideStoryblokPages),
      };
    }

    return {
      href: getPageUrl(language, page),
      ...item,
    };
  });
};

export const filterMenuByExcludedId = (menu: MenuExtendedProps[], excludedId: number) =>
  menu.filter((item) => item.storyblokId !== excludedId);

export const filterOutLanguages = (excludedLanguages: LanguageCode[]) =>
  Array(...languages.keys()).filter((language) => !excludedLanguages.includes(language));
